import React, { useState, useMemo, useEffect } from 'react';
import { 
  ShoppingBag, 
  CheckCircle2, 
  AlertCircle, 
  Sparkles, 
  Plus, 
  Minus, 
  Check, 
  ShieldCheck, 
  Heart, 
  Share2, 
  Info, 
  ChevronDown, 
  ChevronUp,
  Star,
  MessageSquare,
  Send,
  User,
  MapPin,
  Truck,
  ArrowRight,
  BookOpen,
  Tag,
  Copy,
  MessageCircle,
  Clock,
  Layers,
  Award
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useStore } from '../../context/StoreContext';
import { useCart } from '../../context/CartContext';
import { formatPrice, formatMizajDisplay } from '../../utils/formatters';
import { sortVariantsByWeight } from '../../utils/weightPricing';
import { getProductAltText } from '../../utils/seoHelpers';
import ProductCard from './ProductCard';

const RATING_LABELS = {
  5: 'بہترین اور انتہائی مفید (5/5)',
  4: 'بہت اچھا رزلٹ (4/5)',
  3: 'مناسب و تسلی بخش (3/5)',
  2: 'عام نتائج (2/5)',
  1: 'مطمئن نہیں (1/5)'
};

const ProductDetailPage = ({ 
  product: propProduct, 
  onBackToHome, 
  onBackToShop, 
  onSelectProduct,
  onSelectArticle
}) => {
  const { lang, t } = useLanguage();
  const { 
    settings, 
    products, 
    categories, 
    mizajList, 
    reviews, 
    addReview, 
    getProductReviews, 
    getProductRatingStats, 
    orders,
    blogs 
  } = useStore();
  
  const { 
    selectedProduct: contextProduct,
    addToCart, 
    setIsCartOpen, 
    setIsCheckoutOpen 
  } = useCart();

  const product = propProduct || contextProduct;

  // Multi-image gallery state
  const allImages = useMemo(() => {
    if (!product) return [];
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images;
    }
    return [product.image];
  }, [product]);

  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [product?.id]);

  // Active Tab for detailed info: 'benefits', 'usage', 'mizaj', 'reviews'
  const [activeTab, setActiveTab] = useState('benefits');

  // Review Form State
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    customerName: '',
    city: '',
    orderId: '',
    rating: 5,
    comment: ''
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Auto-detect matching order if customer enters an Order ID
  const matchedOrder = useMemo(() => {
    if (!reviewForm.orderId || !orders || orders.length === 0) return null;
    const cleanId = reviewForm.orderId.trim().toUpperCase();
    return orders.find(o => o.id?.toUpperCase() === cleanId || o.id?.toUpperCase() === `HP-${cleanId}`);
  }, [reviewForm.orderId, orders]);

  // Compute live rating & reviews
  const ratingStats = useMemo(() => {
    if (!product) return { rating: 5.0, count: 0 };
    return getProductRatingStats 
      ? getProductRatingStats(product.id, product.rating || 4.9, product.reviewsCount || 24) 
      : { rating: product.rating || 4.9, count: product.reviewsCount || 24 };
  }, [product, reviews, getProductRatingStats]);

  const productReviews = useMemo(() => {
    if (!product) return [];
    return getProductReviews ? getProductReviews(product.id) : [];
  }, [product, reviews, getProductReviews]);

  const handleReviewSubmit = (e) => {
    e.preventDefault();
    if (!reviewForm.comment.trim() || !reviewForm.customerName.trim()) return;

    addReview({
      productId: product.id,
      customerName: reviewForm.customerName,
      city: reviewForm.city,
      orderId: reviewForm.orderId,
      rating: reviewForm.rating,
      comment: reviewForm.comment
    });

    setReviewSubmitted(true);
    setReviewForm({
      customerName: '',
      city: '',
      orderId: '',
      rating: 5,
      comment: ''
    });
    setTimeout(() => {
      setReviewSubmitted(false);
      setShowReviewForm(false);
    }, 2500);
  };

  // Sort variants ascending by weight (50g -> 100g -> 250g -> 500g -> 750g -> 1kg)
  const sortedVariants = useMemo(() => {
    return sortVariantsByWeight(product?.variants || []);
  }, [product]);

  const [selectedVariant, setSelectedVariant] = useState(null);

  useEffect(() => {
    if (sortedVariants.length > 0) {
      setSelectedVariant(sortedVariants[0]);
    } else {
      setSelectedVariant(null);
    }
    setQuantity(1);
    setActiveTab('benefits');
  }, [product?.id]);

  const [quantity, setQuantity] = useState(1);
  const [addedAnimation, setAddedAnimation] = useState(false);

  // Category object
  const currentCategory = useMemo(() => {
    if (!product?.category || !categories) return null;
    return categories.find(c => c.id === product.category);
  }, [product, categories]);

  // Related products (same category or similar, excluding current)
  const relatedProducts = useMemo(() => {
    if (!product || !products) return [];
    return products
      .filter(p => p.id !== product.id && (p.category === product.category || p.mizaj === product.mizaj))
      .slice(0, 4);
  }, [product, products]);

  // Related blog / health article (if any)
  const relatedBlog = useMemo(() => {
    if (!product || !blogs) return null;
    return blogs.find(b => 
      b.status === 'published' && 
      (b.linkedProductId === product.id || 
       (b.focusKeywords && b.focusKeywords.includes(product.nameUrdu.split(' ')[0])) ||
       (b.title && b.title.includes(product.nameUrdu.split(' ')[0]))
      )
    );
  }, [product, blogs]);

  if (!product) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <div className="bg-white rounded-3xl p-8 sm:p-12 border border-gray-100 shadow-sm">
          <div className="w-16 h-16 bg-pansar-50 text-pansar-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-8 h-8" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold font-title text-gray-900 mb-2">کوئی دوا یا پروڈکٹ منتخب نہیں کی گئی</h2>
          <p className="text-gray-500 mb-6 text-xs sm:text-sm font-urdu">براہ کرم اسٹور یا سرچ بار سے کسی دوا کا انتخاب کریں۔</p>
          <button
            onClick={onBackToShop || onBackToHome}
            className="px-6 py-3 bg-pansar-700 hover:bg-pansar-800 text-white rounded-xl font-bold text-sm font-urdu transition-all shadow-md cursor-pointer"
          >
            تمام ادویات دیکھیں
          </button>
        </div>
      </div>
    );
  }

  const currentPrice = selectedVariant ? selectedVariant.price : (sortedVariants[0]?.price || 0);
  const mizajDisplay = formatMizajDisplay(product, mizajList, lang);

  const handleAddToCart = () => {
    addToCart(product, selectedVariant, quantity);
    setAddedAnimation(true);
    setTimeout(() => setAddedAnimation(false), 1500);
  };

  const handleBuyNow = () => {
    addToCart(product, selectedVariant, quantity);
    if (setIsCheckoutOpen) {
      setIsCheckoutOpen(true);
    }
  };

  const handleCopyProductLink = () => {
    const url = window.location.origin + window.location.pathname + `?product=${product.id}`;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  return (
    <div className="min-h-screen bg-gray-50/70 pb-20 pt-4 sm:pt-6 font-urdu" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Breadcrumb Navigation Bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-3.5 sm:p-4 rounded-2xl border border-gray-200/80 shadow-xs text-xs sm:text-sm">
          <nav className="flex items-center gap-1.5 sm:gap-2 text-gray-500 overflow-x-auto whitespace-nowrap scrollbar-none">
            <button
              onClick={onBackToHome}
              className="hover:text-emerald-800 font-bold transition flex items-center gap-1 cursor-pointer"
            >
              <span>صفحہ اول</span>
            </button>
            <span className="text-gray-300">/</span>
            <button
              onClick={onBackToShop}
              className="hover:text-emerald-800 font-bold transition cursor-pointer"
            >
              <span>تمام ادویات</span>
            </button>
            {currentCategory && (
              <>
                <span className="text-gray-300">/</span>
                <span className="text-emerald-800 font-medium">
                  {lang === 'ur' ? currentCategory.nameUrdu : currentCategory.nameEn}
                </span>
              </>
            )}
            <span className="text-gray-300">/</span>
            <span className="text-gray-900 font-bold truncate max-w-[200px] sm:max-w-none">
              {lang === 'ur' ? product.nameUrdu : product.nameEn}
            </span>
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={handleCopyProductLink}
              title="پروڈکٹ کا لنک کاپی کریں"
              className="p-2 rounded-xl bg-gray-100 hover:bg-emerald-50 hover:text-emerald-800 text-gray-600 transition flex items-center gap-1 text-xs cursor-pointer"
            >
              {copiedLink ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              <span className="hidden sm:inline">{copiedLink ? 'کاپی ہو گیا' : 'لنک کاپی کریں'}</span>
            </button>

            <button
              onClick={onBackToShop}
              className="inline-flex items-center gap-1 text-xs font-bold text-emerald-800 hover:text-emerald-950 bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-200 transition cursor-pointer"
            >
              <ArrowRight className="w-3.5 h-3.5" />
              <span>واپس اسٹور</span>
            </button>
          </div>
        </div>

        {/* Main Product Showcase Box (Grid 12 Cols) */}
        <div className="bg-white rounded-3xl p-5 sm:p-8 lg:p-10 border border-gray-200/90 shadow-sm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            
            {/* Left Column: Multi-Image Gallery (5 cols) */}
            <div className="lg:col-span-5 space-y-4">
              
              {/* Main Featured Image Display */}
              <div className="relative aspect-square rounded-3xl overflow-hidden bg-emerald-950/5 border border-gray-200/80 shadow-md group">
                <img
                  src={allImages[activeImageIndex] || product.image}
                  alt={getProductAltText(product, activeImageIndex, lang)}
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                />

                {/* Stock Badge Overlay */}
                <div className="absolute top-4 right-4 flex flex-col gap-2">
                  {product.inStock ? (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-600/95 text-white text-xs font-bold shadow-md backdrop-blur-xs">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      <span>دستیاب باسٹاک</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-rose-600 text-white text-xs font-bold shadow-md">
                      <AlertCircle className="w-3.5 h-3.5" />
                      <span>فی الحال نایاب (Out of Stock)</span>
                    </span>
                  )}
                </div>

                {/* Pure Herbs Guarantee Badge */}
                <div className="absolute bottom-4 right-4">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-black/60 text-amber-300 text-[11px] font-bold backdrop-blur-xs border border-amber-400/30">
                    <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                    <span>100% خالص قدرتی دیسی دوا</span>
                  </span>
                </div>
              </div>

              {/* Thumbnails Gallery Strip (If multiple images exist) */}
              {allImages.length > 1 && (
                <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-none">
                  {allImages.map((imgSrc, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setActiveImageIndex(idx)}
                      className={`w-18 h-18 sm:w-20 sm:h-20 rounded-2xl overflow-hidden border-2 transition-all p-0.5 shrink-0 cursor-pointer ${
                        activeImageIndex === idx 
                          ? 'border-emerald-700 ring-2 ring-emerald-600/30 shadow-md scale-105' 
                          : 'border-gray-200 hover:border-emerald-400 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img 
                        src={imgSrc} 
                        alt={getProductAltText(product, idx, lang)} 
                        className="w-full h-full object-cover rounded-xl"
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Clinic & Fast Delivery Trust Card */}
              <div className="bg-emerald-50/60 rounded-2xl p-4 border border-emerald-200/60 space-y-2.5 text-xs text-emerald-950">
                <div className="flex items-center gap-2.5 font-bold">
                  <Truck className="w-4 h-4 text-emerald-700 shrink-0" />
                  <span>پورے پاکستان کے تمام شہروں میں کیش آن ڈلیوری کی سہولت</span>
                </div>
                <div className="flex items-center gap-2.5 font-bold">
                  <ShieldCheck className="w-4 h-4 text-emerald-700 shrink-0" />
                  <span>حکیم مولانا سید عبدالوہاب شاہ شیرازی کی نگرانی میں تیار کردہ</span>
                </div>
                <div className="flex items-center gap-2.5 font-bold">
                  <Award className="w-4 h-4 text-amber-600 shrink-0" />
                  <span>خالص اجزاء، بغیر کسی نقصان دہ کیمیکل یا اسٹیرائیڈ کے</span>
                </div>
              </div>

            </div>

            {/* Right Column: Buying Options & Product Details (7 cols) */}
            <div className="lg:col-span-7 space-y-6 text-right">
              
              {/* Category & Mizaj Pill */}
              <div className="flex flex-wrap items-center gap-2">
                {currentCategory && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-900 text-xs font-bold border border-emerald-200">
                    <Tag className="w-3.5 h-3.5 text-emerald-700" />
                    <span>{lang === 'ur' ? currentCategory.nameUrdu : currentCategory.nameEn}</span>
                  </span>
                )}
                {mizajDisplay && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-bold border border-amber-200">
                    <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                    <span>مزاج: {mizajDisplay}</span>
                  </span>
                )}
              </div>

              {/* Product Title */}
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 leading-tight">
                  {lang === 'ur' ? product.nameUrdu : product.nameEn}
                </h1>
                {product.nameEn && (
                  <p className="text-sm sm:text-base text-gray-500 font-sans mt-1">
                    {product.nameEn}
                  </p>
                )}
              </div>

              {/* Star Rating Display */}
              <div className="flex items-center gap-3 border-b border-gray-100 pb-4">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      className={`w-4 h-4 sm:w-5 sm:h-5 ${
                        s <= Math.round(ratingStats.rating)
                          ? 'text-amber-500 fill-amber-500'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-xs sm:text-sm font-bold text-gray-700 font-sans">
                  {ratingStats.rating} / 5.0
                </span>
                <span className="text-xs text-gray-400">
                  ({ratingStats.count} کسٹمر ریویوز و تصدیق شدہ رائے)
                </span>
              </div>

              {/* Price Display */}
              <div className="bg-pansar-50/80 rounded-2xl p-4 sm:p-5 border border-pansar-200/70 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <span className="text-xs text-gray-500 block mb-0.5">قیمت برائے منتخب وزن:</span>
                  <div className="text-2xl sm:text-3xl font-black text-pansar-900 font-sans">
                    {formatPrice(currentPrice, lang)}
                  </div>
                </div>

                {selectedVariant && (
                  <div className="text-left">
                    <span className="px-3 py-1 rounded-full bg-emerald-800 text-amber-300 font-bold text-xs shadow-xs">
                      {selectedVariant.weight}
                    </span>
                  </div>
                )}
              </div>

              {/* Weight / Volume Variant Selector */}
              {sortedVariants.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs sm:text-sm font-bold text-gray-800">
                      مطلوبہ وزن یا پیکنگ کا انتخاب کریں:
                    </label>
                    <span className="text-[11px] text-gray-400">
                      (بڑے وزن پر خصوصی رعایت شامل ہے)
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {sortedVariants.map((v, i) => {
                      const isSelected = selectedVariant?.weight === v.weight;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setSelectedVariant(v)}
                          className={`p-3 rounded-2xl border text-right transition cursor-pointer flex flex-col justify-between ${
                            isSelected
                              ? 'bg-emerald-900 text-white border-emerald-900 shadow-md ring-2 ring-emerald-600/30'
                              : 'bg-white hover:bg-emerald-50 text-gray-800 border-gray-200'
                          }`}
                        >
                          <span className="text-xs font-bold leading-snug">
                            {v.weight}
                          </span>
                          <span className={`text-xs font-black font-sans mt-1.5 ${
                            isSelected ? 'text-amber-300' : 'text-emerald-800'
                          }`}>
                            {formatPrice(v.price, lang)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Quantity Selector & Action Buttons */}
              <div className="space-y-4 pt-2">
                <div className="flex flex-wrap items-center gap-4">
                  
                  {/* Quantity Counter */}
                  <div className="flex items-center border-2 border-gray-200 rounded-2xl p-1 bg-gray-50">
                    <button
                      type="button"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-9 h-9 rounded-xl bg-white hover:bg-gray-200 text-gray-800 font-bold flex items-center justify-center transition cursor-pointer shadow-2xs"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-12 text-center font-black text-base font-sans text-gray-900">
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-9 h-9 rounded-xl bg-white hover:bg-gray-200 text-gray-800 font-bold flex items-center justify-center transition cursor-pointer shadow-2xs"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Add to Cart Button */}
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    disabled={!product.inStock}
                    className={`flex-1 py-3.5 px-6 rounded-2xl font-bold text-sm shadow-md transition flex items-center justify-center gap-2 ${
                      !product.inStock
                        ? 'bg-rose-50 text-rose-700 border border-rose-200 cursor-not-allowed opacity-90'
                        : addedAnimation
                        ? 'bg-emerald-600 text-white cursor-pointer'
                        : 'bg-emerald-800 hover:bg-emerald-900 text-white cursor-pointer'
                    }`}
                  >
                    {addedAnimation ? (
                      <>
                        <CheckCircle2 className="w-5 h-5 text-white" />
                        <span>کارٹ میں شامل کر دیا گیا!</span>
                      </>
                    ) : !product.inStock ? (
                      <>
                        <AlertCircle className="w-5 h-5 text-rose-500" />
                        <span>اسٹاک ختم ہے (آؤٹ آف اسٹاک)</span>
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="w-5 h-5" />
                        <span>کارٹ میں شامل کریں</span>
                      </>
                    )}
                  </button>

                  {/* Buy Now (Direct Checkout) Button */}
                  {product.inStock && (
                    <button
                      type="button"
                      onClick={handleBuyNow}
                      className="w-full sm:w-auto py-3.5 px-8 rounded-2xl bg-amber-500 hover:bg-amber-600 text-gray-950 font-black text-sm shadow-md transition flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <Sparkles className="w-4 h-4 text-gray-950" />
                      <span>ابھی آرڈر کریں (Buy Now)</span>
                    </button>
                  )}
                </div>
              </div>

            </div>

          </div>
        </div>

        {/* Detailed Tabs Section (Benefits, Dosage, Tibb & Mizaj, Customer Reviews) */}
        <div className="bg-white rounded-3xl border border-gray-200/90 shadow-sm overflow-hidden">
          
          {/* Tab Headers */}
          <div className="flex border-b border-gray-200 bg-gray-50/70 p-2 overflow-x-auto scrollbar-none gap-2">
            <button
              onClick={() => setActiveTab('benefits')}
              className={`py-3 px-5 rounded-2xl text-xs sm:text-sm font-bold transition whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                activeTab === 'benefits'
                  ? 'bg-emerald-900 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span>فوائد و طبی خواص</span>
            </button>

            <button
              onClick={() => setActiveTab('usage')}
              className={`py-3 px-5 rounded-2xl text-xs sm:text-sm font-bold transition whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                activeTab === 'usage'
                  ? 'bg-emerald-900 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Clock className="w-4 h-4 text-emerald-400" />
              <span>طریقہ استعمال و مقدار خوراک</span>
            </button>

            <button
              onClick={() => setActiveTab('mizaj')}
              className={`py-3 px-5 rounded-2xl text-xs sm:text-sm font-bold transition whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                activeTab === 'mizaj'
                  ? 'bg-emerald-900 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Layers className="w-4 h-4 text-emerald-400" />
              <span>قانون مفرد اعضاء و مزاج گائیڈ</span>
            </button>

            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-3 px-5 rounded-2xl text-xs sm:text-sm font-bold transition whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                activeTab === 'reviews'
                  ? 'bg-emerald-900 text-white shadow-sm'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span>کسٹمر ریویوز ({ratingStats.count})</span>
            </button>
          </div>

          {/* Tab Content Body */}
          <div className="p-6 sm:p-10 text-right leading-relaxed">
            
            {/* TAB 1: BENEFITS */}
            {activeTab === 'benefits' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    <span>طبی فوائد اور جسمانی اثرات</span>
                  </h3>
                  <div className="text-sm sm:text-base text-gray-700 leading-relaxed sm:leading-loose whitespace-pre-line bg-gray-50/50 p-6 rounded-2xl border border-gray-100">
                    {product.benefitsUrdu || 'یہ ایک خالص نباتاتی مرکب ہے جو جسم کے اعضاء رئیسہ (دل، دماغ، جگر) کو تقویت فراہم کرتا ہے اور قدرتی دفاعی نظام کو مضبوط بناتا ہے۔'}
                  </div>
                </div>

                {product.benefitsEn && (
                  <div className="pt-4 border-t border-gray-100 text-left dir-ltr">
                    <h4 className="text-sm font-bold text-gray-800 font-sans mb-1">
                      Key Health Benefits (English):
                    </h4>
                    <p className="text-xs sm:text-sm text-gray-600 font-sans leading-relaxed">
                      {product.benefitsEn}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* TAB 2: USAGE & DOSAGE */}
            {activeTab === 'usage' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-emerald-700" />
                    <span>طریقہ استعمال اور احتیاطی تدابیر</span>
                  </h3>
                  <div className="text-sm sm:text-base text-gray-700 leading-relaxed sm:leading-loose whitespace-pre-line bg-emerald-50/40 p-6 rounded-2xl border border-emerald-100">
                    {product.usageUrdu || 'معالج کی ہدایت کے مطابق نہار منہ یا بعد از طعام مناسب بدرقہ (نیم گرم پانی یا دودھ) کے ساتھ استعمال کریں۔'}
                  </div>
                </div>

                {product.precautionsUrdu && (
                  <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-950 text-xs sm:text-sm">
                    <div className="font-bold flex items-center gap-1.5 mb-1 text-amber-900">
                      <AlertCircle className="w-4 h-4 text-amber-700" />
                      <span>ضروری احتیاط:</span>
                    </div>
                    <p>{product.precautionsUrdu}</p>
                  </div>
                )}
              </div>
            )}

            {/* TAB 3: TIBB & MIZAJ */}
            {activeTab === 'mizaj' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-emerald-700" />
                    <span>قانون مفرد اعضاء اور مزاج کی تفصیل</span>
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-5 rounded-2xl bg-emerald-50 border border-emerald-200 space-y-2">
                      <span className="text-xs text-emerald-800 font-bold block">مقررہ مزاج:</span>
                      <div className="text-lg font-bold text-emerald-950">
                        {mizajDisplay || 'معتدل'}
                      </div>
                      <p className="text-xs text-emerald-800 leading-relaxed">
                        یہ دوا جسم کے متاثرہ عضو میں تحریک پیدا کر کے افعال کو درست سمت میں لاتی ہے اور غیر طبعی اخلاط کو خارج کرتی ہے۔
                      </p>
                    </div>

                    <div className="p-5 rounded-2xl bg-gray-50 border border-gray-200 space-y-2">
                      <span className="text-xs text-gray-600 font-bold block">طبی اصول صابر ملتانیؒ:</span>
                      <p className="text-xs text-gray-700 leading-relaxed">
                        دوا کا استعمال مرض کے اصل سبب (تحریک، تسکین، تحلیل) کے مطابق کیا جانا چاہیے۔ کسی پیچیدہ مرض کی صورت میں حکیم صاحب سے نبض کا معائنہ یا آن لائن مشورہ ضرور لیں۔
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* TAB 4: REVIEWS */}
            {activeTab === 'reviews' && (
              <div className="space-y-8">
                
                {/* Rating Summary Header */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-amber-50/50 p-6 rounded-3xl border border-amber-200/60">
                  <div className="text-center sm:text-right">
                    <div className="text-3xl sm:text-4xl font-black text-amber-900 font-sans">
                      {ratingStats.rating} <span className="text-lg text-amber-700">/ 5.0</span>
                    </div>
                    <div className="flex items-center justify-center sm:justify-start gap-1 my-1">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star
                          key={s}
                          className={`w-4 h-4 ${
                            s <= Math.round(ratingStats.rating)
                              ? 'text-amber-500 fill-amber-500'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-amber-900 font-medium">
                      کل {ratingStats.count} کسٹمرز کے حقیقی تاثرات اور ریویوز
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowReviewForm(!showReviewForm)}
                    className="px-6 py-3 rounded-2xl bg-emerald-800 hover:bg-emerald-900 text-white font-bold text-xs sm:text-sm shadow-md transition cursor-pointer flex items-center gap-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>{showReviewForm ? 'ریویو فارم بند کریں' : 'اپنا ریویو اور رائے درج کریں'}</span>
                  </button>
                </div>

                {/* Review Submission Form */}
                {showReviewForm && (
                  <form onSubmit={handleReviewSubmit} className="bg-white p-6 rounded-3xl border border-emerald-200 shadow-md space-y-4 animate-fade-in">
                    <h4 className="font-bold text-base text-gray-900 border-b border-gray-100 pb-3">
                      اس پروڈکٹ کے بارے میں اپنا حقیقی تجربہ شیئر کریں
                    </h4>

                    {reviewSubmitted && (
                      <div className="p-4 rounded-2xl bg-emerald-100 text-emerald-900 font-bold text-xs flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-emerald-700" />
                        <span>آپ کا ریویو موصول ہو گیا ہے اور کامیابی سے شامل کر دیا گیا ہے۔ شکریہ!</span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div>
                        <label className="text-xs font-bold text-gray-700 block mb-1">
                          آپ کا نام *
                        </label>
                        <input
                          type="text"
                          required
                          value={reviewForm.customerName}
                          onChange={(e) => setReviewForm({ ...reviewForm, customerName: e.target.value })}
                          placeholder="مثلاً: محمد عثمان"
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-xs focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-700 block mb-1">
                          شہر کا نام
                        </label>
                        <input
                          type="text"
                          value={reviewForm.city}
                          onChange={(e) => setReviewForm({ ...reviewForm, city: e.target.value })}
                          placeholder="مثلاً: لاہور، راولپنڈی"
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-xs focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-700 block mb-1">
                          آرڈر نمبر (اختیاری)
                        </label>
                        <input
                          type="text"
                          value={reviewForm.orderId}
                          onChange={(e) => setReviewForm({ ...reviewForm, orderId: e.target.value })}
                          placeholder="مثلاً: HP-1082"
                          className="w-full p-2.5 rounded-xl border border-gray-200 text-xs font-sans focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                        />
                      </div>
                    </div>

                    {/* Star Rating Picker */}
                    <div>
                      <label className="text-xs font-bold text-gray-700 block mb-1.5">
                        اسٹار ریٹنگ منتخب کریں:
                      </label>
                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(0)}
                            className="p-1 cursor-pointer transition hover:scale-110"
                          >
                            <Star
                              className={`w-7 h-7 ${
                                star <= (hoverRating || reviewForm.rating)
                                  ? 'text-amber-500 fill-amber-500'
                                  : 'text-gray-300'
                              }`}
                            />
                          </button>
                        ))}
                        <span className="text-xs text-gray-600 mr-2 font-bold">
                          {RATING_LABELS[hoverRating || reviewForm.rating]}
                        </span>
                      </div>
                    </div>

                    {/* Review Comment */}
                    <div>
                      <label className="text-xs font-bold text-gray-700 block mb-1">
                        آپ کا تفصیلی تبصرہ و نتائج *
                      </label>
                      <textarea
                        required
                        rows="3"
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                        placeholder="آپ نے یہ دوا کیسے استعمال کی اور کیا نتائج ملے؟"
                        className="w-full p-3 rounded-xl border border-gray-200 text-xs focus:ring-2 focus:ring-emerald-600 focus:outline-none"
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setShowReviewForm(false)}
                        className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-xs font-bold cursor-pointer"
                      >
                        منسوخ
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2 rounded-xl bg-emerald-800 hover:bg-emerald-900 text-white text-xs font-bold transition shadow-sm cursor-pointer"
                      >
                        ریویو جمع کروائیں
                      </button>
                    </div>
                  </form>
                )}

                {/* Reviews List */}
                <div className="space-y-4">
                  {productReviews.length > 0 ? (
                    productReviews.map((rev) => (
                      <div
                        key={rev.id}
                        className="p-5 rounded-2xl bg-gray-50/70 border border-gray-200/80 space-y-2.5 text-right"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200/60 pb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-emerald-800 text-white flex items-center justify-center font-bold text-xs">
                              {rev.customerName?.charAt(0) || 'ک'}
                            </div>
                            <div>
                              <div className="font-bold text-xs sm:text-sm text-gray-900 flex items-center gap-1.5">
                                <span>{rev.customerName}</span>
                                {rev.verified && (
                                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold">
                                    ✓ تصدیق شدہ خریدار
                                  </span>
                                )}
                              </div>
                              {rev.city && (
                                <span className="text-[11px] text-gray-400">
                                  {rev.city}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map((st) => (
                              <Star
                                key={st}
                                className={`w-3.5 h-3.5 ${
                                  st <= (rev.rating || 5)
                                    ? 'text-amber-500 fill-amber-500'
                                    : 'text-gray-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>

                        <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
                          {rev.comment}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center bg-gray-50 rounded-2xl border border-gray-200 text-gray-500 text-xs">
                      فی الحال کوئی تفصیلی ریویو موجود نہیں۔ آپ پہلے خریدار بن کر اپنا تجربہ شیئر کریں!
                    </div>
                  )}
                </div>

              </div>
            )}

          </div>

        </div>

        {/* Linked Health Article Box (If Available) */}
        {relatedBlog && (
          <div 
            onClick={() => onSelectArticle && onSelectArticle(relatedBlog)}
            className="bg-gradient-to-r from-emerald-900 via-emerald-850 to-pansar-900 text-white rounded-3xl p-6 sm:p-8 shadow-md flex flex-col md:flex-row items-center justify-between gap-6 cursor-pointer group border border-emerald-700 hover:border-amber-400 transition"
          >
            <div className="space-y-2 max-w-2xl text-right">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-800/90 text-amber-300 text-xs font-bold">
                <BookOpen className="w-3.5 h-3.5" />
                <span>حکیم صاحب کا خاص معلوماتی مقالہ</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold font-urdu group-hover:text-amber-200 transition">
                {relatedBlog.title}
              </h3>
              <p className="text-xs sm:text-sm text-emerald-100 font-urdu line-clamp-2">
                {relatedBlog.excerpt}
              </p>
            </div>

            <button
              type="button"
              className="px-6 py-3 rounded-2xl bg-amber-500 hover:bg-amber-600 text-gray-950 font-black text-xs sm:text-sm shadow-md transition font-urdu shrink-0 cursor-pointer"
            >
              مضمون پڑھیں
            </button>
          </div>
        )}

        {/* Related Products Section (ملتی جلتی مزید ادویات) */}
        {relatedProducts.length > 0 && (
          <div className="space-y-6 pt-4">
            <div className="flex items-center justify-between border-b border-gray-200 pb-4">
              <div className="text-right">
                <h3 className="text-xl sm:text-2xl font-black text-gray-900 font-urdu">
                  ملتی جلتی مزید خالص ادویات و جڑی بوٹیاں
                </h3>
                <p className="text-xs text-gray-500 font-urdu mt-0.5">
                  اسی شعبے اور مزاج سے متعلق دیگر مقبول ادویات
                </p>
              </div>

              <button
                type="button"
                onClick={onBackToShop}
                className="text-xs font-bold text-emerald-800 hover:text-emerald-950 font-urdu bg-emerald-50 px-3.5 py-1.5 rounded-xl border border-emerald-200 cursor-pointer"
              >
                تمام ادویات دیکھیں
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relProd) => (
                <div 
                  key={relProd.id}
                  onClick={() => {
                    if (onSelectProduct) {
                      onSelectProduct(relProd);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }
                  }}
                >
                  <ProductCard product={relProd} />
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ProductDetailPage;
